import React from "react";
import axios, { AxiosResponse } from "axios";
import { AlertColor, Button, MenuItem, TextField, useMediaQuery} from "@mui/material";
import { Add, Edit } from "@mui/icons-material";
import { DataGridPro, GridColumns, GridRowId, GridSelectionModel, GridToolbarContainer, GridValueSetterParams } from '@mui/x-data-grid-pro';
import Title from "../../component/Title";
import { globalTheme } from "../../../common/theme";
import { useConstructor } from "../../../common/UseConstructor";
import { useAppSelector } from "../../../common/hooks";
import { RootState } from "../../../common/Store";
import { CommonProps } from "../../component/MainContents";
import { getUserPreferences, renewToken } from "../../../services/AuthService";
import { SnackBarMessage, SnackBarMessageProps } from "../../component/SnackBarMessage";
import { CustomTablePagination } from "../../component/CustomPagination";
import { EndUserSingle, EndUserSingleHandle } from "./EndUserSingle";
import { OpenMode } from "../../../common/Enums";
import { EndUserListItem } from "./EndUserAccessories";
import { EndUserFilter } from "../../../models/classes/EndUserFilter";
import { BaseControl, DropDownComboControl, GenericFilter } from "../../component/GenericFilter";

export function EndUserManagement(props: CommonProps) {
	const [loading, setLoading] = React.useState(true);
	const [hasError, setHasError] = React.useState(false);

    const isDebug = useAppSelector((state: RootState) => state.menuToggle.debugMode);

	const singleModalRef = React.useRef<EndUserSingleHandle>(null);

    const style = { ...globalTheme() };
     
    const tinyScreen = useMediaQuery('(max-width:1366px)');
    const smallScreen = useMediaQuery('(max-width:1920px)');
    const normalScreen = useMediaQuery('(max-width:2560px)');
	
	const [controls, setControls] = React.useState<BaseControl[]>([]); //For filtering

	const hasChanges = React.useRef<boolean>(false);
  
	const [searchData, setSearchData] = React.useState<any>({
		contains: true,
		searchText: "",
		customer: 0,
		region: "ALL",
		pageSize: 10,
	});

    //Dialogs and snacks
    const [snackSettings, setSnackSettings] =
      React.useState<SnackBarMessageProps>({
        open: false,
        message: "",
        subMessage: "",
        severity: undefined,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });

	const [gridData, setGridData] = React.useState<EndUserListItem[]>([]);
	const [selectionModel, setSelectionModel] = React.useState<GridRowId[]>(() =>
		gridData.filter((r) => r.selected).map((r) => r.id),
	);
	const [selectedEndUser, setSelectedEndUser] = React.useState<EndUserListItem>();

    function showSnackBar(message: string, subMessage: string, colour?: AlertColor) {
      setSnackSettings({
        open: true,
        message: message,
        subMessage: subMessage,
        severity: colour,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });
    }

	useConstructor(() => Initialise())
    function Initialise() {
        setLoading(true);
		renewToken();

		setControls([
            {
              key: 'customer',
              title: 'Customer',
              dropdownOptions: props.customers
            } as DropDownComboControl,
            {
              key: 'region',
              title: 'Region',
              dropdownOptions: props.regions
            } as DropDownComboControl
          ]);    
  
		LoadData();
		setSelectionModel([]);
		  
		hasChanges.current = false;

	}

	function NewEndUser() {
		let userPreferences = getUserPreferences();
		let endUser: EndUserListItem = {
			id: 0,
			endUserId: 0,
			endUserName: "",
			endUserEng: "",
			customerName: "",
			customerID: 0,
			region: userPreferences?.defaultRegion ?? "HK",
			selected: false
		};
		singleModalRef.current?.Initialise(OpenMode.NEW, endUser);
	}

	function EditEndUser(endUserId: number) {
		let endUser = gridData.find(x => x.endUserId == endUserId);
		if (endUser)
			singleModalRef.current?.Initialise(OpenMode.EDIT, endUser);
	}

	function DoSearch(event: React.FormEvent<any>) {
        if (event)
          event.preventDefault();
  
        LoadData();
	}

	function LoadData() {
        setLoading(true);
		
        let filter = new EndUserFilter({
            EndUser: searchData.searchText,
            CustomerID: searchData.customer % 100000,
            Region: searchData.region,
            Reader: ""
          }
        );
  
        axios.post<EndUserListItem[]>('Api/V1/EndUser/Search', filter, {
          headers: {
            'Content-Encoding': 'gzip'
          }
        })
          .then((result) => {
            result.data.forEach(i => {
              i.id = i.endUserId;
              i.selected = false;
            });
            setGridData(result.data)
            setLoading(false);
          },
            (error) => {
              setGridData([]);
              setLoading(false);
              console.log(error);
              showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
            }
          )
          .catch((error) => {
            setLoading(false);
            console.log(error);
            showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
          });
	}

	//Grid columns
	const columns: GridColumns = [
		{ field: 'endUserName', headerName: 'End User Name', flex: 5, type: 'string' },
		{ field: 'endUserEng', headerName: 'End User English Name', flex: 5, type: 'string' },
		{ field: 'customerName', headerName: 'Customer Name', flex: 4, type: 'string' },
		{ field: 'region', headerName: 'Region', flex: 1, type: 'string' },
	];

	function OnSelectionChanged(model: GridSelectionModel) {
        const ids = new Set(selectionModel);
        if (model.length > 1) {
          setSelectionModel(model.filter((s) => !ids.has(s)));
        } 
        else {
          setSelectionModel(model);
        }
		setSelectedEndUser(gridData.find(x => x.endUserId===model.filter((s) => !ids.has(s))[0]));
	}

	function CustomToolbar() {
		return (
			<GridToolbarContainer style={{ display: 'flex' }}>
				<div style={{ flexGrow: 1, minWidth: "10px" }}></div>
				<Button style={{ width: "120px", minWidth: "100px" }}
					variant="contained" size="small" color="secondary" startIcon={<Add />} onClick={() => NewEndUser()}>Add</Button>
				<div style={{ width: "10px", minWidth: "10px" }}></div>
				<Button style={{ width: "120px", minWidth: "100px" }} disabled={ selectedEndUser?.endUserId==0 }
					variant="contained" size="small" color="secondary" startIcon={<Edit />} onClick={(e) => EditEndUser(selectedEndUser?.endUserId ?? 0)}>Edit</Button>
				<div style={{ width: "10px", minWidth: "10px" }}></div>
			</GridToolbarContainer>
		);
	}

    return (
        <div className={style.PageMain}>
			<Title pageTitle="End User" />
			<div className={style.PageContent}>
				<GenericFilter controls={controls} searchData={searchData} setSearchData={setSearchData} doSearch={DoSearch} searchBoxLabel="End User Name"/>
				<div className={style.ModalContent}>
					<div className={style.ModalFields} style={{ height: "100%", minHeight: "200px" }}>
						<DataGridPro
							getRowId={(row) => row.id}
							rows={gridData}
							columns={columns}
							pageSize={searchData.pageSize}
							onPageSizeChange={(pageSize) => setSearchData({ ...searchData, pageSize: pageSize })}
							pagination
							rowsPerPageOptions={[10, 30, 50, 100]}
							loading={loading}
							checkboxSelection
							components={{
								Toolbar: CustomToolbar,
								Footer: CustomTablePagination
							}}
							componentsProps={{
								footer: { rowsPerPageOptions: [10, 30, 50, 100] }
							}}
							selectionModel={selectionModel}
							onSelectionModelChange={(selectionModel) => {
								OnSelectionChanged(selectionModel)
							}}
							rowHeight={tinyScreen ? 20 : (smallScreen ? 24 : 28)}
							onRowDoubleClick={(params) => EditEndUser(params.row.endUserId)}
						/>
					</div>
				</div>
			</div>

			<EndUserSingle
				ref={singleModalRef}
				onClose={(saved: boolean, endUser: EndUserListItem) => {
					if (saved && endUser.endUserId > 0)
						LoadData();
				}} 
				customers={props.customers} 
				endUsers={[]} 
				regions={props.regions} 
				sales={[]} 
				deliveryMethods={[]} 
				paymentTerms={[]} 
				currencies={[]}
			/> 

			<SnackBarMessage {...snackSettings} />

        </div>
    );
}

